import type { PlatformBridge } from './PlatformBridge';
import { nativeKeepAwake } from './iosKeepAwake';

interface WebkitWindow {
  webkit?: { messageHandlers?: Record<string, { postMessage(body: unknown): void }> };
}

/**
 * iOS App Clip shell — the same WKWebView code as the paid app, loading the same deployed
 * bundle, but installed on demand from a link or a Safari card and never bought. See
 * specs/app-clip.md.
 *
 * Two deliberate differences from `iosBridge`:
 *  - no `requestReview`: nothing has been bought, so there is nothing to rate (and Apple's
 *    sheet in a trial would burn the once-per-90-days ask on a non-customer);
 *  - `requestUpsell`, which the paid app has no use for.
 */
export const iosClipBridge: PlatformBridge = {
  platform: 'ios-clip',
  // Routed to the clip's "upsell" handler, which shows StoreKit's SKOverlay App Clip banner.
  // Apple's own UI on purpose: the overlay installs the paid app in place rather than kicking
  // the player out to the App Store, and it is the only sanctioned way for a clip to sell its
  // full app. Fire-and-forget, like requestReview — StoreKit reports nothing back.
  requestUpsell: () => {
    try {
      (window as unknown as WebkitWindow).webkit?.messageHandlers?.upsell?.postMessage({});
    } catch { /* handler not registered (older clip build) — the card just has no primary action */ }
  },
  // Shared with the paid app, deliberately: a clip is a trial of the same game, and a screen that
  // locks mid-board is exactly the impression it must not leave. Same handler, same Swift file,
  // and the same fall-back-to-navigator.wakeLock rule on a clip built before it existed.
  get keepAwake(): ((on: boolean) => void) | undefined {
    return nativeKeepAwake();
  },
};
